import { apiOrganizationLinks } from "@/api/organization-link.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiOrganizationLinkPayload } from "@/types/organization-link.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const organizationLinksKeys = createQueryKeys("organizationLinks", {
  all: null,
});

export const useOrganizationLinks = () => {
  return useQuery({
    ...organizationLinksKeys.all,
    queryFn: async () => {
      const result = await apiOrganizationLinks.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
  });
};

export interface OrganizationLinkSaveParams {
  id?: number;
  payload: ApiOrganizationLinkPayload;
}

export const useOrganizationLinkSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: OrganizationLinkSaveParams) => {
      const result = id
        ? await apiOrganizationLinks.update(id, payload)
        : await apiOrganizationLinks.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationLinksKeys.all.queryKey });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useOrganizationLinkDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiOrganizationLinks.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationLinksKeys.all.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useOrganizationLinksUpdateSort = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (ids: number[]) => {
      const result = await apiOrganizationLinks.updateSort({ ids });
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationLinksKeys.all.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
